import { useState, useEffect } from "react";
import { TextField, Button, Typography, InputAdornment, IconButton } from "@mui/material";
import API from "../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";

function VoucherEntry() {
    
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const [voucherNo, setVoucherNo] = useState("");
    const [voucherDate, setVoucherDate] = useState("");
    const [errors, setErrors] = useState({});
    const [place, setPlace] = useState("");
    const [paidTo, setPaidTo] = useState("");
    const [activeFilter, setActiveFilter] = useState("");
    const [details, setDetails] = useState([
        { description: "", amount: "" }
    ]);
    const total = details.reduce(
        (sum, row) => sum + (parseFloat(row.amount) || 0),
        0
    );
    const [voucherList, setVoucherList] = useState([]);
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [sortField, setSortField] = useState("id");
    const [sortOrder, setSortOrder] = useState("desc");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const highlightText = (text) => {
        if (!search) return text;

        const parts = text.toString().split(
            new RegExp(`(${search})`, "gi")
        );

        return parts.map((part, i) =>
            part.toLowerCase() === search.toLowerCase()
                ? <span key={i} style={{ backgroundColor: "yellow" }}>{part}</span>
                : part
        );
    };

    const addRow = () => {
        setDetails([...details, { description: "", amount: "" }]);
    };

    const deleteRow = (index) => {

        // 🚫 Prevent deleting last row
        if (details.length === 1) {
            alert("At least one row is required");
            return;
        }

        const updated = [...details];
        updated.splice(index, 1);
        setDetails(updated);
    };

    const handleChange = (index, field, value) => {
        const updated = [...details];
        updated[index][field] = value;
        setDetails(updated);
    };

    const handleSave = async () => {
        try {

            // ✅ VALIDATION FIRST (before anything else)
            let newErrors = {};

            if (!voucherDate) {
                newErrors.voucherDate = "Date required";
            }

            if (!paidTo) {
                newErrors.paidTo = "Paid To is required";
            }

            const validDetails = details
                .filter(d => d.description && d.amount)
                .map(d => ({
                    description: d.description,
                    amount: Number(d.amount)
                }));

            if (validDetails.length === 0) {
                newErrors.details = "Add at least one valid row";
            }

            setErrors(newErrors);

            // 🚫 STOP if validation fails (NO API CALL)
            if (Object.keys(newErrors).length > 0) {
                return;
            }

            // ✅ SAFE PAYLOAD
            const payload = {
                VoucherDate: voucherDate,
                Place: place || "",
                PaidTo: paidTo || "",
                Details: validDetails
            };

            let res;

            if (isEdit) {
                await API.put(`/Voucher/${id}`, payload);

                // ✅ success message (optional replace with snackbar later)
                alert("Voucher updated successfully");

                navigate(`/app/voucher-print/${id}`);

            } else {
                res = await API.post(`/Voucher`, payload);

                navigate(`/app/voucher-print/${res.data.id}`);
            }

        } catch (err) {
            console.error("FULL ERROR:", err);

            // ✅ better fallback message
            if (err.response?.data) {
                alert(err.response.data);
            } else {
                alert("Something went wrong. Please try again.");
            }
        }
    };

    const loadNextVoucherNo = async () => {
        try {
            const res = await API.get("/Voucher/next-number");
            setVoucherNo(res.data);
        } catch (err) {
            console.error("Error loading voucher number", err);
        }
    };

    const loadVouchers = async () => {
        try {
            const res = await API.get("/Voucher/list");
            setVoucherList(res.data);
        } catch (err) {
            console.error("Error loading vouchers", err);
        }
    };
    useEffect(() => {
        if (isEdit) {
            loadVoucher();
        } else {
            loadNextVoucherNo();
        }

        loadVouchers();
    }, [id]);   // 🔥 THIS IS THE FIX

    useEffect(() => {
        setPage(1);
    }, [search, fromDate, toDate]);

    
    const today = new Date();
    const last7Days = new Date();
    last7Days.setDate(today.getDate() - 7);

    const filteredData = voucherList
        .filter(v => {
            const vDate = new Date(v.voucherDate);

            const from = fromDate ? new Date(fromDate) : null;
            const to = toDate ? new Date(toDate) : null;

            const dateMatch =
                (!from || vDate >= from) &&
                (!to || vDate <= to);

            return (
                dateMatch &&
                (
                    v.paidTo?.toLowerCase().includes(search.toLowerCase()) ||
                    String(v.voucherNo).includes(search) ||
                    String(v.totalAmount).includes(search) ||
                    new Date(v.voucherDate).toLocaleDateString("en-IN").includes(search)
                )
            );
        })
        .sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (typeof valA === "string") valA = valA.toLowerCase();
            if (typeof valB === "string") valB = valB.toLowerCase();

            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });

    const paginatedData = [...filteredData].slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    useEffect(() => {
        const totalPages = Math.ceil(filteredData.length / rowsPerPage);

        if (page > totalPages && totalPages > 0) {
            setPage(1);
        }
    }, [filteredData, rowsPerPage]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const exportToExcel = () => {
        const data = filteredData.map(v => ({
            "Voucher No": v.voucherNo,
            "Date": new Date(v.voucherDate).toLocaleDateString("en-IN"),
            "Paid To": v.paidTo,
            "Amount": v.totalAmount
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const file = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        saveAs(file, "Vouchers.xlsx");
    };
    const handleFilter = () => {
        if (!fromDate || !toDate) {
            alert("Select both dates");
            return;
        }

        setPage(1);
    };
    const handleDelete = async (id, voucherNo) => {

        // ✅ CONFIRMATION (BEFORE API CALL)
        if (!window.confirm(`Delete Voucher #${voucherNo}?`)) return;

        try {
            await API.delete(`/Voucher/${id}`);

            // ✅ SUCCESS MESSAGE (AFTER DELETE)
            alert("Voucher deleted successfully");

            // ✅ REFRESH GRID
            loadVouchers();

        } catch (err) {
            alert("Error deleting voucher");
        }
    };

    const loadVoucher = async () => {
        try {
            const res = await API.get(`/Voucher/get/${id}`);
            const data = res.data;

            setVoucherNo(data.voucherNo);
            setVoucherDate(data.voucherDate.split("T")[0]);
            setPlace(data.place);
            setPaidTo(data.paidTo);

            setDetails(
                data.details.map(d => ({
                    description: d.description,
                    amount: d.amount
                }))
            );

        } catch (err) {
            alert("Error loading voucher");
        }
    };
    return (
        <div style={styles.container}>
            <div style={styles.entrySection}>
                {/* 🔵 HEADER BAR */}
                <div style={styles.header}>
                    {isEdit ? "Edit Voucher" : "Voucher Entry"}
                </div>
                <div style={styles.filterWrapper}>
                    <div style={styles.filterBar}>

                        <TextField
                            type="date"
                            value={fromDate}
                            onChange={(e) => {
                                setFromDate(e.target.value);
                                setActiveFilter("");
                            }}
                            size="small"
                            sx={{
                                width: "140px", // use 200px for search
                                "& .MuiOutlinedInput-root": {
                                    height: "28px",
                                    fontFamily: "Verdana"
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "8px",
                                    fontSize: "12px"
                                }
                            }}
                        />

                        <TextField
                            type="date"
                            value={toDate}
                            onChange={(e) => {
                                setToDate(e.target.value);
                                setActiveFilter("");   // 👈 THIS LINE ADDED
                            }}
                            size="small"
                            sx={{
                                width: "140px", // use 200px for search
                                "& .MuiOutlinedInput-root": {
                                    height: "28px",
                                    fontFamily: "Verdana"
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "8px",
                                    fontSize: "12px"
                                }
                            }}
                        />

                        <button
                            style={styles.filterBtn}
                            onClick={() => {
                                handleFilter();
                                setActiveFilter("");
                            }}
                        >
                            Filter
                        </button>

                        <button
                            style={activeFilter === "last7" ? styles.last7BtnActive : styles.last7Btn}
                            onClick={() => {
                                const today = new Date();
                                const last7 = new Date();
                                last7.setDate(today.getDate() - 7);

                                const from = last7.toISOString().split("T")[0];
                                const to = today.toISOString().split("T")[0];

                                // ✅ SINGLE STATE FLOW (important)
                                setFromDate(from);
                                setToDate(to);
                                setPage(1);

                                // ⬇️ DELAY THIS (this is the key fix)
                                setTimeout(() => {
                                    setActiveFilter("last7");
                                }, 0);
                            }}
                        >
                            Last 7 Days
                        </button>

                        <button
                            style={styles.resetBtn}
                            onClick={() => {
                                setSearch("");
                                setFromDate("");
                                setToDate("");
                                setPage(1);
                                setActiveFilter("");   // 🔥 important
                            }}
                        >
                            Reset
                        </button>

                        <TextField
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => {
                                console.log("SEARCH:", e.target.value);   // ✅ ADD HERE
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            size="small"
                            sx={{
                                width: "140px", // use 200px for search
                                "& .MuiOutlinedInput-root": {
                                    height: "28px",
                                    fontFamily: "Verdana"
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "8px",
                                    fontSize: "12px"
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: search && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearch("")}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <button style={styles.exportBtn} onClick={exportToExcel}>
                            Export
                        </button>

                        <div style={styles.rowsBox}>
                            Rows per page:
                            <select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setPage(1);
                                }}
                                style={styles.rowsSelect}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </div>

                    </div>
                </div>

                {/* Divider */}
                <div style={styles.divider}></div>
                {/* 🔹 FORM BOX */}
                <div style={styles.card}>

                    {/* ROW 1 */}
                    <div style={styles.row}>
                        <TextField
                            label="Voucher No"
                            value={voucherNo}
                            onChange={e => setVoucherNo(e.target.value)}
                            fullWidth
                            size="small"
                            sx={styles.input}
                            disabled
                        />
                        <TextField
                            type="date"
                            label="Date"
                            value={voucherDate}
                            onChange={e => setVoucherDate(e.target.value)}
                            error={!!errors.voucherDate}
                            helperText={errors.voucherDate}
                            fullWidth
                            size="small"
                            sx={styles.input}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Place"
                            value={place}
                            onChange={e => setPlace(e.target.value)}
                            fullWidth
                            size="small"
                            sx={styles.input}
                        />
                        <TextField
                            label="Paid To"
                            value={paidTo}
                            onChange={e => setPaidTo(e.target.value)}
                            error={!!errors.paidTo}
                            helperText={errors.paidTo}
                            fullWidth
                            size="small"
                            sx={styles.input}
                        />
                    </div>

                    {/* TABLE */}
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Description</th>
                                    <th style={styles.th}>Amount</th>
                                    <th style={styles.th}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.map((row, i) => (
                                    <tr key={i}>
                                        <td style={{ ...styles.td, paddingLeft: "0px" }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                sx={styles.input}
                                                value={row.description}
                                                onChange={(e) =>
                                                    handleChange(i, "description", e.target.value)
                                                }
                                            />
                                        </td>
                                        <td style={styles.td}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                sx={styles.input}
                                                type="number"
                                                value={row.amount}
                                                onChange={(e) =>
                                                    handleChange(i, "amount", e.target.value)
                                                }
                                            />
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.actionWrap}>

                                                <DeleteIcon
                                                    style={styles.deleteIcon}
                                                    onClick={() => deleteRow(i)}
                                                />

                                                <div
                                                    style={styles.addInline}
                                                    onClick={addRow}
                                                    onMouseOver={e => e.currentTarget.style.color = "#20bf6b"}
                                                    onMouseOut={e => e.currentTarget.style.color = "#2ed573"}
                                                >
                                                    <AddIcon style={{ fontSize: "14px" }} />
                                                    <span>Add Row</span>
                                                </div>

                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {errors.details && (
                            <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
                                {errors.details}
                            </div>
                        )}

                        <div style={styles.pagination}>
                            <button
                                style={styles.pageBtn}
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                            >
                                Prev
                            </button>

                            <span style={styles.pageText}>
                                Page {page} of {totalPages}
                            </span>

                            <button
                                style={styles.pageBtn}
                                disabled={page === totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                Next
                            </button>
                        </div>

                        {/* ADD ROW */}
                        {/*<div style={styles.addRow} onClick={addRow}>*/}
                        {/*    <AddIcon style={{ fontSize: "18px", color: "#1976d2" }} />*/}
                        {/*    <span style={{ marginLeft: "6px" }}>Add Row</span>*/}
                        {/*</div>*/}

                        {/*<div style={styles.addRow} onClick={addRow}>*/}
                        {/*    <AddIcon style={{ fontSize: "16px" }} />*/}
                        {/*    <span style={{ marginLeft: "6px" }}>Add Row</span>*/}
                        {/*</div>*/}

                        {/* TOTAL */}
                        {/*<div style={styles.total}>*/}
                        {/*    Total: ₹ {totalAmount.toFixed(2)}*/}
                        {/*</div>*/}
                        <div style={styles.bottomRow}>
                            <div style={styles.totalCard}>
                                Total: ₹ {total.toFixed(2)}
                            </div>
                        </div>

                        {/* SAVE BUTTON */}
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            sx={{
                                marginTop: "10px",
                                height: "28px",
                                minHeight: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana",
                                padding: "0 12px",
                                textTransform: "none"
                            }}
                        >
                            {isEdit ? "Update" : "Save"}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/app/voucher")}
                            sx={{
                                marginTop: "10px",
                                marginLeft: "10px",
                                height: "28px",
                                fontSize: "12px",
                                textTransform: "none"
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
            {/* 🔹 LIST SECTION START */}
            <div style={styles.listSection}>

                <table style={styles.gridTable}>
                    <thead>
                        <tr>
                            <th
                                style={styles.gridTh}
                                onClick={() => handleSort("voucherNo")}
                            >
                                V.No {sortField === "voucherNo" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </th>

                            <th
                                style={styles.gridTh}
                                onClick={() => handleSort("voucherDate")}
                            >
                                Date {sortField === "voucherDate" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </th>

                            <th
                                style={styles.gridTh}
                                onClick={() => handleSort("paidTo")}
                            >
                                Paid To {sortField === "paidTo" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </th>

                            <th
                                style={styles.gridThRight}
                                onClick={() => handleSort("totalAmount")}
                            >
                                Amount {sortField === "totalAmount" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </th>

                            <th style={styles.gridTh}>
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((v, i) => (
                            <tr
                                key={v.id}
                                style={{
                                    ...styles.gridRow,
                                    background: i % 2 === 0 ? "#fafafa" : "#fff"
                                }}
                                onMouseOver={e => e.currentTarget.style.background = "#f1f2f6"}
                                onMouseOut={e => e.currentTarget.style.background = i % 2 === 0 ? "#fafafa" : "#fff"}
                            >
                                <td style={styles.gridTd}>
                                    {highlightText(v.voucherNo)}
                                </td>
                                <td style={styles.gridTd}>
                                    {highlightText(new Date(v.voucherDate).toLocaleDateString("en-IN"))}
                                </td>
                                <td style={styles.gridTd}>
                                    {highlightText(v.paidTo)}
                                </td>
                                <td style={styles.gridTdRight}>
                                    ₹ {highlightText(Number(v.totalAmount).toFixed(2))}
                                </td>
                                <td style={styles.gridTd}>
                                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>

                                        {/* VIEW */}
                                        <Tooltip title="View">
                                            <VisibilityIcon
                                                style={{ fontSize: "18px", color: "#1976d2", cursor: "pointer" }}
                                                onMouseOver={e => (e.currentTarget.style.color = "#0d47a1")}
                                                onMouseOut={e => (e.currentTarget.style.color = "#1976d2")}
                                                onClick={() => navigate(`/app/voucher-print/${v.id}`)}
                                            />
                                        </Tooltip>

                                        {/* EDIT */}
                                        <Tooltip title="Edit">
                                            <EditIcon
                                                style={{ fontSize: "18px", color: "#2e7d32", cursor: "pointer" }}
                                                onMouseOver={e => (e.currentTarget.style.color = "#1b5e20")}
                                                onMouseOut={e => (e.currentTarget.style.color = "#2e7d32")}
                                                onClick={() => {
                                                    console.log("EDIT CLICKED", v.id);   // 🔍 debug
                                                    navigate(`/app/voucher-edit/${v.id}`);
                                                }}
                                            />
                                        </Tooltip>

                                        {/* DELETE */}
                                        <Tooltip title="Delete">
                                            <DeleteIcon
                                                style={{ fontSize: "18px", color: "#d32f2f", cursor: "pointer" }}
                                                onMouseOver={e => (e.currentTarget.style.color = "#9a0007")}
                                                onMouseOut={e => (e.currentTarget.style.color = "#d32f2f")}
                                                onClick={() => handleDelete(v.id, v.voucherNo)}
                                            />
                                        </Tooltip>

                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>  
    );
}
const styles = {

    header: {
        background: "#3f4654",
        color: "#fff",
        padding: "12px",
        fontSize: "14px",
        fontWeight: "bold",
        borderRadius: "6px",
        marginBottom: "10px",
        width: "100%",
        maxWidth: "1100px"   // must match card/filter
    },

    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "15px"
    },

    entrySection: {
        width: "100%",
        maxWidth: "1100px"
    },

    card: {
        width: "100%",
        maxWidth: "1100px",
        background: "#fff",
        padding: "10px",   // 🔥 SAME AS FILTER
        borderRadius: "6px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    },

    /* FILTER BAR */
    filterBar: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "12px"
    },

    commonBtn: {
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
        borderRadius: "4px",
        fontSize: "12px",
        cursor: "pointer",
        border: "none"
    },

    filterBtn: {
        height: "28px",
        background: "#2ed573",
        color: "#fff",
        padding: "0 12px",
        borderRadius: "4px",
        fontSize: "12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center"
    },

    last7Btn: {
        height: "28px",
        background: "#2ed573",
        color: "#fff",
        padding: "0 12px",
        borderRadius: "4px",
        fontSize: "12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center"
    },

    resetBtn: {
        height: "28px",
        background: "#ff4757",
        color: "#fff",
        padding: "0 12px",
        borderRadius: "4px",
        fontSize: "12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center"
    },

    exportBtn: {
        height: "28px",
        background: "#1e90ff",
        color: "#fff",
        padding: "0 12px",
        borderRadius: "4px",
        fontSize: "12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center"
    },

    rowsBox: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "12px"
    },

    rowsSelect: {
        height: "28px",
        fontSize: "12px",
        borderRadius: "4px",
        border: "1px solid #ccc",
        padding: "0 6px"
    },

    /* FORM GRID */
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: "12px",
        marginBottom: "12px"
    },

    input: {
        "& .MuiInputBase-root": {
            height: "32px",
            fontSize: "12px",
            background: "#fff"
        },
        "& .MuiInputBase-input": {
            fontSize: "12px",
            padding: "6px 8px"
        },
        "& .MuiInputLabel-root": {
            fontSize: "12px"
        }
    },

    /* TABLE */
    tableContainer: {
        background: "#fff",
        padding: "0px",   // 🔥 REMOVE EXTRA SHIFT
        borderRadius: "6px"
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "12px",
        marginLeft: "0px"   // 🔥 ensure no shift
    },

    th: {
        background: "#4a5160",
        color: "#fff",
        padding: "10px",   // 🔥 remove left gap
        textAlign: "left",
        fontSize: "12px"
    },

    td: {
        padding: "8px",   // 🔥 remove left gap
        borderBottom: "none",
        fontSize: "12px"
    },

    total: {
        width: "25%",                 // 🔥 match Amount column width
        textAlign: "right",
        fontWeight: "bold",
        fontSize: "12px"
    },

    addRow: {
        marginTop: "10px",
        color: "#1976d2",
        cursor: "pointer",
        fontSize: "12px",
        display: "flex",
        alignItems: "center"
    },

    saveBtn: {
        height: "28px",
        background: "#1976d2",
        textTransform: "none",
        fontSize: "12px",
        padding: "0 16px",
        borderRadius: "6px",
        boxShadow: "none",
        "&:hover": {
            background: "#1565c0"
        }
    },

    pagination: {
        marginTop: "10px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        justifyContent: "center",
        fontSize: "12px"
    },
    pageBtn: {
        height: "28px",
        padding: "0 10px",
        border: "1px solid #ccc",
        background: "#fff",
        cursor: "pointer",
        borderRadius: "3px",
        fontSize: "12px"
    },

    pageText: {
        fontSize: "12px"
    },

    /* GRID LIST */
    listSection: {
        marginTop: "10px",
        background: "#fff",
        padding: "10px",
        borderRadius: "6px",
        maxWidth: "1100px",
        width: "100%"
    },

    gridTable: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "12px",
        border: "1px solid #ccc"   // outer border
    },

    gridTh: {
        background: "#3f4654",
        color: "#fff",
        padding: "8px",
        textAlign: "left",
        fontWeight: "600",
        border: "1px solid #ccc"   // header borders
    },

    gridThRight: {
        background: "#3f4654",
        color: "#fff",
        padding: "8px",
        textAlign: "right",
        fontWeight: "600",
        border: "1px solid #ccc"
    },

    gridTd: {
        padding: "8px",
        border: "1px solid #ddd"   // cell borders
    },

    gridTdRight: {
        padding: "8px",
        border: "1px solid #ddd",
        textAlign: "right"
    },

    viewLink: {
        color: "#1976d2",
        cursor: "pointer",
        fontWeight: "500"
    },
    filterWrapper: {
        width: "100%",
        maxWidth: "1100px",
        background: "#fff",
        padding: "10px",   // SAME as card
        borderRadius: "6px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        marginBottom: "10px"
    },
    bottomRow: {
        display: "flex",
        justifyContent: "center",   // 🔥 center under table
        marginTop: "10px"
    },

    totalCard: {
        minWidth: "180px",
        textAlign: "right",
        padding: "6px 12px",
        border: "1px solid #c8e6c9",        // soft green border
        borderRadius: "6px",
        background: "#e8f5e9",              // light green background
        color: "#1b5e20",                   // dark green text
        fontWeight: "bold",
        fontSize: "12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
    },

    last7BtnActive: {
        height: "28px",
        background: "#145a32",
        color: "#fff",
        padding: "0 12px",
        borderRadius: "4px",
        fontSize: "12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        border: "1px solid #0e3d24",
        boxShadow: "inset 0 0 3px rgba(0,0,0,0.4)"
    },
    actionWrap: {
        display: "flex",
        alignItems: "center",
        gap: "10px"
    },

    deleteIcon: {
        cursor: "pointer",
        fontSize: "18px",
        color: "#1976d2"
    },

    addInline: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        cursor: "pointer",
        color: "#2ed573",
        fontSize: "12px",
        fontWeight: "500"
    },
      
};


export default VoucherEntry;